import { API_ENDPOINTS, ApiResponse } from 'services/api';
import httpClient from 'services/httpClient';
import { CourseCombineData } from 'types/lcms/courseCombine';

interface Params {
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  status?: string;
  sortBy?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: 'asc' | 'desc';
  year?: number;
}

interface TrainingCatalogueRawItem {
  trainingRecommendationID: string;
  moduleCode: string;
  courseName: string;
  programName: string;
  year?: number;
  learningObjectives?: string | null;
  deliveryID?: string;
  deliveryName?: string;
  competencyCategoryID?: string | null;
  competencyCategoryName?: string | null;
  learningHours?: number;
  competencyID?: string;
  competencyName?: string;
  trainingMethodID?: string | null;
  trainingMethodName?: string | null;
  targetParticipant?: string;
  moduleReadiness?: string;
}

interface TrainingCatalogueResponse extends ApiResponse<TrainingCatalogueRawItem> {}

const defaultResponse: ApiResponse<CourseCombineData> = {
  pageNumber: 1,
  pageSize: 10,
  totalPages: 0,
  totalRecords: 0,
  message: 'Failed to fetch data',
  data: []
};

// Map raw API item to existing CourseCombineData interface used by table.
// Assumptions (adjust if backend changes):
// 1. moduleCode uniquely identifies a training catalogue entry and is reused for both courseId & courseCode.
// 2. programName conceptually matches previous "courseFrom" dummy field.
// 3. The API response (sample provided) does not include updated/created metadata; placeholders ('') used.
// 4. Additional fields (learningObjectives, deliveryName, etc.) are currently not displayed but can be
//    added by extending CourseCombineData and updating table columns later.
export const trainingCatalogueService = {
  list: async (params?: Params): Promise<ApiResponse<CourseCombineData>> => {
    try {
      const response = await httpClient.get<TrainingCatalogueResponse>(
        API_ENDPOINTS.LCMS.TRAINING_CATALOGUE.LIST,
        {
          params: { ...params }
        }
      );

      const mapped: CourseCombineData[] = response.data.data.map((item) => ({
        trainingRecommendationID: item.trainingRecommendationID,
        courseId: item.trainingRecommendationID,
        courseCode: item.moduleCode,
        courseName: item.courseName,
        courseFrom: item.programName || '',
        year: item.year,
        updateAt: '',
        createdBy: ''
      }));

      return {
        pageNumber: response.data.pageNumber,
        pageSize: response.data.pageSize,
        totalPages: response.data.totalPages,
        totalRecords: response.data.totalRecords,
        message: response.data.message,
        data: mapped
      };
    } catch {
      return {
        ...defaultResponse,
        pageNumber: params?.pageNumber || 1,
        pageSize: params?.pageSize || 10
      };
    }
  }
};
// Detail mapping types
interface TrainingCatalogueDetailRaw {
  trainingRecommendationID: string;
  pnaYear: number;
  strategicObjectiveId: string;
  strategicObjectives: string;
  performanceMeasures: string;
  moduleCode: string;
  courseName: string;
  trainingCategoryDesc: string;
  learningObjectives: string | null;
  trainingMethodName: string;
  learningHours: number;
  activityCode: string | null;
  budget: number;
  totalCost: number;
  trainingProvider: string;
  schedule: string;
  scheduleDesc: string;
  competencyName: string;
  competencyDevelopDesc: string;
  levelImpactDesc: string;
  programName: string;
  trainingModelDesc: string;
  // Legacy fields for backward compatibility
  deliveryID?: string;
  deliveryName?: string;
  competencyCategoryID?: string | null;
  competencyCategoryName?: string | null;
  competencyID?: string;
  trainingMethodID?: string | null;
  targetParticipant?: string;
  moduleReadiness?: string;
}

interface TrainingCatalogueDetailResponse {
  message: string;
  data: TrainingCatalogueDetailRaw;
}

// Competency detail types
interface TrainingCatalogueCompetencyDetailRaw {
  competencyCategoryDesc: string;
  urgencyDesc: string;
}

interface TrainingCatalogueCompetencyDetailResponse {
  message: string;
  data: TrainingCatalogueCompetencyDetailRaw;
}

export const trainingCatalogueDetailService = {
  detail: async (trainingRecommendationID: string) => {
    try {
      const response = await httpClient.get<TrainingCatalogueDetailResponse>(
        `${API_ENDPOINTS.LCMS.TRAINING_CATALOGUE.DETAIL}/${trainingRecommendationID}`
      );
      return {
        message: response.data.message,
        data: response.data.data
      } as const;
    } catch (e: any) {
      return {
        message: e?.message || 'Failed to fetch detail',
        data: undefined
      } as const;
    }
  }
};

export const trainingCatalogueCompetencyDetailService = {
  detail: async (trainingRecommendationID: string) => {
    try {
      const response = await httpClient.get<TrainingCatalogueCompetencyDetailResponse>(
        `${API_ENDPOINTS.LCMS.TRAINING_CATALOGUE.DETAIL_COMPETENCY}/${trainingRecommendationID}`
      );
      return {
        message: response.data.message,
        data: response.data.data
      } as const;
    } catch (e: any) {
      return {
        message: e?.message || 'Failed to fetch competency detail',
        data: undefined
      } as const;
    }
  }
};

// Participants types and service
interface TrainingCatalogueParticipantRaw {
  binusianId: string;
  kodeDosen?: string | null;
  participantName: string;
  participantPosition?: string | null;
  participantLevel?: string | null;
  participantType?: string | null;
}

interface TrainingCatalogueParticipantsResponse {
  message: string;
  data: TrainingCatalogueParticipantRaw[];
}

export const trainingCatalogueParticipantsService = {
  list: async (trainingRecommendationID: string) => {
    try {
      const response = await httpClient.get<TrainingCatalogueParticipantsResponse>(
        `${API_ENDPOINTS.LCMS.TRAINING_CATALOGUE.PARTICIPANTS}?trainingRecommendationId=${trainingRecommendationID}`
      );
      return {
        message: response.data.message,
        data: response.data.data
      } as const;
    } catch (e: any) {
      return {
        message: e?.message || 'Failed to fetch participants',
        data: [] as TrainingCatalogueParticipantRaw[]
      } as const;
    }
  }
};
